import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { ArrowLeft, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import type { ViewType360 } from "@/types/exterior360";

const SEGMENTS_TOTAL = 72;
const TARGET_WIDTH = 1280;
const TARGET_HEIGHT = 720;
/** Same white square overlay as video recorder: 70% height, centered */
const OVERLAY_HEIGHT_PERCENT = 0.7;

interface Exterior360CameraViewLabels {
  landscapeHint?: string;
  /** Hint when taking single image from pie slice (not video) */
  singleImageHint?: string;
  takePhoto?: string;
  retake?: string;
  next?: string;
  done?: string;
  uploading?: string;
  uploadProgress?: string;
  waitForUpload?: string;
  startCamera?: string;
  cameraStarting?: string;
  cameraError?: string;
  fallbackLabel?: string;
  deleteImage?: string;
  backToPicker?: string;
  frameIndex?: string;
  imageAlreadyExists?: string;
  allCompleteTitle?: string;
  allCompleteDescription?: string;
  failedCount?: string;
}

interface Exterior360CameraViewProps {
  viewType: ViewType360;
  onCapture: (blob: Blob) => void;
  onRetake?: () => void;
  onNext?: () => void;
  onComplete?: () => void;
  onDelete?: () => void;
  onBack?: () => void;
  canComplete?: boolean;
  capturedBlob: Blob | null;
  totalSteps: number;
  currentStepIndex: number;
  /** Segment mode: frameIndex 0-23, next wraps. */
  frameIndex?: number;
  totalFrames?: number;
  /** Existing image for this frame (show preview + delete). */
  existingImageUrl?: string;
  /** When all 24 segments have images - show success overlay. */
  allSegmentsComplete?: boolean;
  uploadStatus?: { done: number; total: number; failed: number };
  labels?: Exterior360CameraViewLabels;
}

export function Exterior360CameraView({
  viewType,
  onCapture,
  onRetake,
  onNext,
  onComplete,
  onDelete,
  onBack,
  canComplete = true,
  capturedBlob,
  totalSteps,
  currentStepIndex,
  frameIndex: frameIndexProp,
  totalFrames = SEGMENTS_TOTAL,
  existingImageUrl,
  allSegmentsComplete = false,
  uploadStatus,
  labels = {},
}: Exterior360CameraViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);
  const [useFileFallback, setUseFileFallback] = useState(false);

  const segmentMode = frameIndexProp != null;
  const frameIndex = segmentMode ? frameIndexProp : currentStepIndex;

  const startStream = useCallback(async () => {
    if (streamRef.current) return;
    setIsStarting(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: TARGET_WIDTH, min: 640 },
          height: { ideal: TARGET_HEIGHT, min: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setStreamReady(true); // Först sätta streamReady så att video-elementet renderas
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`${labels.cameraError ?? "Could not start camera"}: ${msg}`);
      setUseFileFallback(true);
    } finally {
      setIsStarting(false);
    }
  }, [labels.cameraError]);

  // Videon renderas när streamReady=true. Vi kopplar stream till video här när båda finns.
  useEffect(() => {
    if (!streamReady || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    video.srcObject = stream;
    video.play().catch(() => {});
  }, [streamReady]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreamReady(false);
  }, []);

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current || video.readyState < 2) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(blob);
      },
      "image/jpeg",
      0.95, // Higher quality for 360 capture – fullsize target 150-200KB
    );
  }, [onCapture]);

  const isLastStep = !segmentMode && currentStepIndex >= totalSteps - 1;
  const hasExistingImage = Boolean(existingImageUrl);
  const hasCaptured = capturedBlob !== null;
  const showExistingOnly = hasExistingImage && !hasCaptured;
  const showSuccessOverlay = segmentMode && allSegmentsComplete;

  return (
    <div className="flex flex-col h-full">
      <div
        ref={containerRef}
        className="relative w-full flex-1 min-h-[160px] max-h-[45vh] sm:max-h-none bg-slate-900 rounded-2xl overflow-hidden ring-2 ring-slate-700/50 ring-offset-2 ring-offset-slate-100 dark:ring-offset-slate-900 shadow-xl shrink-0"
        style={{ aspectRatio: "16/9" }}
      >
        {showSuccessOverlay && (
          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-sm">
            <div className="relative">
              <CheckCircle2 className="h-20 w-20 text-green-500 animate-in zoom-in duration-500" />
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
              <div className="absolute inset-0 bg-green-500/20 rounded-full animate-pulse" />
            </div>
            <p className="mt-4 text-xl font-bold text-green-400 animate-in fade-in duration-500">
              {labels.allCompleteTitle ?? "Klart!"}
            </p>
            <p className="mt-1 text-sm text-slate-300 animate-in fade-in duration-700">
              {labels.allCompleteDescription ??
                "Alla vyer är tagna. Du kan stänga."}
            </p>
            {onComplete && (
              <Button
                onClick={onComplete}
                className="mt-6 rounded-xl font-semibold"
                size="lg"
              >
                {labels.done ?? "Klart – stäng"}
              </Button>
            )}
          </div>
        )}
        {showExistingOnly && existingImageUrl && !showSuccessOverlay && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/95 z-20">
            <img
              src={existingImageUrl}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
            <p className="text-sm text-emerald-400 mt-2 font-medium">
              {labels.imageAlreadyExists ?? "Bild finns redan"}
            </p>
            {segmentMode && (
              <p className="text-xs text-slate-400 mt-1">
                {labels.frameIndex ?? "Vinkel"} {frameIndex + 1}/{totalFrames}
              </p>
            )}
          </div>
        )}
        {!streamReady && !error && !showExistingOnly && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/95 z-10">
            {isStarting ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="h-14 w-14 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                  <Loader2 className="absolute inset-0 m-auto h-7 w-7 text-primary animate-spin" />
                </div>
                <p className="text-sm text-slate-300 animate-pulse">
                  {labels.cameraStarting ?? "Startar kamera..."}
                </p>
              </div>
            ) : (
              <Button
                onClick={startStream}
                size="lg"
                className="h-12 px-8 rounded-xl font-semibold shadow-lg shadow-primary/25"
              >
                {labels.startCamera ?? "Starta kamera"}
              </Button>
            )}
          </div>
        )}
        {error && useFileFallback && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 bg-slate-900/95 z-10">
            <p className="text-sm text-center text-red-300">{error}</p>
            <p className="text-xs text-slate-400">
              {labels.fallbackLabel ?? "Fallback: ladda upp bild istället"}
            </p>
            <FileUploadTrigger
              accept="image/*"
              preferCamera
              onFilesSelected={(files) => {
                const f = files[0];
                if (f) {
                  f.arrayBuffer().then((buf) => {
                    onCapture(new Blob([buf], { type: f.type }));
                  });
                }
              }}
            >
              {(open) => (
                <Button
                  type="button"
                  variant="secondary"
                  className="text-sm"
                  onClick={open}
                >
                  {labels.fallbackLabel ?? "Fallback: ladda upp bild istället"}
                </Button>
              )}
            </FileUploadTrigger>
          </div>
        )}
        {error && !useFileFallback && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-red-300 p-6 bg-slate-900/95 z-10">
            <p className="text-sm text-center">{error}</p>
          </div>
        )}
        {segmentMode && (streamReady || hasCaptured || showExistingOnly) && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-lg bg-black/60 text-white text-sm font-medium">
            {labels.frameIndex ?? "Vinkel"} {frameIndex + 1}/{totalFrames}
          </div>
        )}
        {streamReady && !showExistingOnly && (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectFit: "cover" }}
            />
            {/* Same white square overlay as video recorder – 70% height, centered */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none border-2 border-white/80 rounded-lg shadow-lg"
              style={{
                height: `${OVERLAY_HEIGHT_PERCENT * 100}%`,
                width: `${OVERLAY_HEIGHT_PERCENT * 100}%`,
                minWidth: 0,
                minHeight: 0,
              }}
              aria-hidden
            />
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {uploadStatus && uploadStatus.total > 0 && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800/80 text-xs text-slate-600 dark:text-slate-400 shrink-0">
          {labels.uploadProgress ?? "Uppladdning"}: {uploadStatus.done}/
          {uploadStatus.total}
          {uploadStatus.failed > 0 && (
            <span className="text-destructive ml-1">
              ({uploadStatus.failed} {labels.failedCount ?? "failed"})
            </span>
          )}
        </div>
      )}

      {showExistingOnly && (
        <div className="flex gap-2 mt-2 sm:mt-4 flex-wrap shrink-0">
          <Button
            variant="outline"
            onClick={startStream}
            className="rounded-xl border-2"
          >
            {labels.retake ?? "Ta om"}
          </Button>
          {onDelete && (
            <Button
              variant="outline"
              onClick={onDelete}
              className="rounded-xl border-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {labels.deleteImage ?? "Radera bild"}
            </Button>
          )}
          {onNext && (
            <Button
              onClick={onNext}
              className="flex-1 h-11 rounded-xl font-semibold"
              size="lg"
            >
              {labels.next ?? "Nästa"}
            </Button>
          )}
          {onBack && (
            <Button variant="ghost" onClick={onBack} className="rounded-xl">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {labels.backToPicker ?? "Tillbaka"}
            </Button>
          )}
        </div>
      )}
      {streamReady && !showExistingOnly && (
        <div className="flex flex-col gap-2 shrink-0">
          <p className="text-xs text-muted-foreground text-center">
            {segmentMode
              ? (labels.singleImageHint ??
                "Ta en bild med hela bilen inom den vita rutan. Detta är en enskild bild – inte video.")
              : (labels.landscapeHint ??
                (labels as { videoOverlayHint?: string }).videoOverlayHint ??
                "Håll telefonen liggande. Håll hela bilen inom den vita rutan.")}
          </p>
          <div className="flex gap-2 flex-wrap">
            {!hasCaptured ? (
              <Button
                onClick={capture}
                className="flex-1 h-11 rounded-xl font-semibold shadow-md hover:shadow-lg transition-shadow"
                size="lg"
              >
                {labels.takePhoto ?? "Ta bild"}
              </Button>
            ) : (
              <>
                {onRetake && (
                  <Button
                    variant="outline"
                    onClick={onRetake}
                    className="rounded-xl border-2"
                  >
                    {labels.retake ?? "Ta om"}
                  </Button>
                )}
                {onNext && (segmentMode || !isLastStep) && (
                  <Button
                    onClick={onNext}
                    className="flex-1 h-11 rounded-xl font-semibold"
                    size="lg"
                  >
                    {labels.next ?? "Nästa"}
                  </Button>
                )}
                {onComplete && !segmentMode && isLastStep && (
                  <Button
                    onClick={onComplete}
                    className="flex-1 h-11 rounded-xl font-semibold shadow-md hover:shadow-lg disabled:opacity-70"
                    size="lg"
                    disabled={!canComplete}
                    title={
                      !canComplete && uploadStatus?.total
                        ? (labels.waitForUpload ??
                          "Vänta tills uppladdning är klar")
                        : undefined
                    }
                  >
                    {!canComplete && uploadStatus?.total
                      ? `${labels.uploading ?? "Laddar upp…"} ${uploadStatus.done}/${uploadStatus.total}`
                      : (labels.done ?? "Klart – stäng")}
                  </Button>
                )}
                {segmentMode && onBack && (
                  <Button
                    variant="ghost"
                    onClick={onBack}
                    className="rounded-xl"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {labels.backToPicker ?? "Tillbaka"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
